import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Save, Upload, Plus, Minus, Trash2, Check, UserPlus, PackagePlus } from "lucide-react";
import Markdown from "../components/Markdown";

const CAREERS = ["Colonial Marine", "Colonial Marshall", "Company Agent", "Kid", "Medic", "Officer", "Pilot", "Roughneck", "Scientist"];
const ATTRS = [
  { key: "strength", name: "Strength", skills: [["close_combat","Close Combat"],["heavy_machinery","Heavy Machinery"],["stamina","Stamina"]] },
  { key: "agility", name: "Agility", skills: [["mobility","Mobility"],["piloting","Piloting"],["ranged_combat","Ranged Combat"]] },
  { key: "wits", name: "Wits", skills: [["comtech","Comtech"],["observation","Observation"],["survival","Survival"]] },
  { key: "empathy", name: "Empathy", skills: [["command","Command"],["manipulation","Manipulation"],["medical_aid","Medical Aid"]] },
];
const DEFAULT_TOKEN = "https://images.unsplash.com/photo-1766941234616-ba978cb000e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxyZXRybyUyMGNvbXB1dGVyJTIwdGVybWluYWx8ZW58MHx8fHwxNzc3NTk4MDkyfDA&ixlib=rb-4.1.0&q=85";

function Field({ label, children }) {
  return <div><label className="label-term mb-1 block">{label}</label>{children}</div>;
}

function ShopPurchasePanel({ character, campaign, canBypass, onPurchased }) {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("weapon");
  useEffect(() => {
    api.get(`/shop?item_type=${tab}`).then(r => setItems(r.data)).catch(()=>{});
  }, [tab]);
  const shopOpen = campaign?.shop_open && campaign?.session_active;
  const canBuy = canBypass || shopOpen;
  const buy = async (iid) => {
    try {
      const r = await api.post(`/shop/${iid}/purchase?character_id=${character.id}`);
      toast.success(`Purchased. Party cash: $${r.data.party_cash}`);
      onPurchased?.(r.data.party_cash);
    } catch (e) { toast.error(e?.response?.data?.detail || "Purchase failed"); }
  };
  return (
    <div className="panel p-5" data-testid="char-shop-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="label-term amber">// Quick Purchase</div>
        <div className="mono text-xs">
          {canBypass ? <span className="neon-green">GM/Admin — always open</span> :
            (shopOpen ? <span className="neon-green">● Store Open</span> : <span className="text-[#FF2A2A]">○ Store Closed</span>)}
          <span className="ml-3 amber">Party: ${campaign?.party_cash || 0}</span>
        </div>
      </div>
      <div className="flex gap-1 mb-3 border-b border-[#333]">
        {["weapon","armor","gear","vehicle"].map(t => (
          <button key={t} onClick={()=>setTab(t)} className={`px-3 py-1 mono text-xs uppercase tracking-widest ${tab===t?"amber border-b-2 border-[#FFB000]":"text-[#A0A0A0]"}`}>{t}</button>
        ))}
      </div>
      {!canBuy && <div className="mono text-xs text-[#A0A0A0] mb-3">// The GM must open the store during a live session before you can buy.</div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-80 overflow-y-auto">
        {items.length === 0 && <div className="mono text-xs text-[#A0A0A0]">// no items</div>}
        {items.map(i => (
          <div key={i.id} className="border border-[#222] p-2 flex items-center justify-between gap-2" data-testid={`char-buy-${i.id}`}>
            <div className="flex-1 min-w-0">
              <div className="amber mono text-sm truncate">{i.name}</div>
              <div className="mono text-[10px] text-[#A0A0A0]">${i.price}</div>
              <div className="mono text-[9px] text-[#A0A0A0]">{i.description}</div>
            </div>
            <button className="btn-amber text-xs" disabled={!canBuy} onClick={()=>buy(i.id)}>Buy</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CharacterDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [campaignChars, setCampaignChars] = useState([]);
  const [friendsData, setFriendsData] = useState({ friends: [], requests: [] });
  const [tab, setTab] = useState("stats");
  const [tokenUrl, setTokenUrl] = useState(null);
  const [cashDelta, setCashDelta] = useState(0);
  const [editBackstory, setEditBackstory] = useState(false);
  const fileInput = useRef(null);
  const [storeItems, setStoreItems] = useState([]);
  const [itemMap, setItemMap] = useState({});
  const load = async () => {
    try {
      const r = await api.get(`/characters/${id}`);
      setC(r.data);
      if (r.data.token_path) setTokenUrl(fileUrl(r.data.token_path));
      if (r.data.campaign_id) {
        try {
          const [c2, chrs, shp] = await Promise.all([
            api.get(`/campaigns/${r.data.campaign_id}`),
            api.get(`/campaigns/${r.data.campaign_id}/characters`),
            api.get(`/shop?item_type=${tab}`),
          ]);
          setCampaign(c2.data);
          setCampaignChars(chrs.data.filter(x => x.id !== id));
        } catch {}
      }
      // Load friends if owner
      if (r.data.owner_id === user?.id) {
        try { const fr = await api.get(`/characters/${id}/friends`); setFriendsData(fr.data); } catch {}
      }
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!c) return <div className="p-8 mono">// Loading...</div>;

  const isOwner = user?.id === c.owner_id;
  const isGM = campaign?.gm_id === user?.id;
  const isAdmin = user?.role === "admin";
  const canEditGmFields = isGM || isAdmin;
  const canEditBasic = isOwner || canEditGmFields;  // most fields
  const canEditStats = isOwner || canEditGmFields;  // most fields
  const canEditName = isAdmin;                       // only global admin
  const canEditCareer = isGM || isAdmin;                     // only global admin
  const canEditCash = isOwner || isGM || isAdmin;

  const set = (k, v) => setC({ ...c, [k]: v });
  const setAttr = (k, v) => setC({ ...c, attributes: { ...c.attributes, [k]: Number(v) } });
  const setSkill = (k, v) => setC({ ...c, skills: { ...c.skills, [k]: Number(v) } });

  const save = async () => {
    try { await api.put(`/characters/${id}`, c); toast.success("Saved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const uploadToken = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post(`/characters/${id}/token`, fd, { headers: { "Content-Type":"multipart/form-data" }});
      toast.success("Token uploaded");
      setTokenUrl(fileUrl(r.data.token_path));
      setC({ ...c, token_path: r.data.token_path });
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
  };

  const addCash = async (delta) => {
    try {
      const r = await api.post(`/characters/${id}/cash`, { amount: delta });
      setC({ ...c, cash: r.data.cash });
      toast.success(`${delta >= 0 ? "+" : ""}${delta} credits`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const acceptFR = async (reqCid) => {
    try { await api.post(`/characters/${id}/friend-accept/${reqCid}`); toast.success("Accepted"); load(); } catch {}
  };
  const removeWeapon = (i) => set("weapons", c.weapons.filter((_,x)=>x!==i));
  const removeGear = (i) => set("gear", c.gear.filter((_,x)=>x!==i));
  const clearArmor = () => set("armor", { name:"", level:0, weight:0 });

  const stowOnShip = async (listName, idx) => {
    if (!c.campaign_id) { toast.error("Character must be in a campaign to stow"); return; }
    try {
      await api.post(`/campaigns/${c.campaign_id}/ship/transfer`, { direction:"to_ship", character_id: c.id, char_list: listName, char_index: idx });
      const r = await api.get(`/characters/${id}`); setC(r.data);
      toast.success("Stowed on ship");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Aggregate active temp_buffs by stat name for visual cues
  const buffMap = (c?.temp_buffs || []).reduce((acc, b) => {
    const k = (b.stat || "").toLowerCase();
    if (!k) return acc;
    acc[k] = (acc[k] || 0) + (Number(b.value) || 0);
    return acc;
  }, {});
  const buffSign = (n) => (n >= 0 ? `+${n}` : `${n}`);

  const consumeGear = async (i) => {
    try {
      const r = await api.post(`/characters/${id}/use-item`, { list_name: "gear", index: i });
      toast.success(r.data?.consumed ? "Item used (consumed)" : "Effects applied");
      setC(r.data.character);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const clearBuff = async (buffId) => {
    try {
      const r = await api.delete(`/characters/${id}/temp-buff/${buffId}`);
      setC({ ...c, temp_buffs: r.data.temp_buffs });
    } catch { toast.error("Failed"); }
  };
  const removeVehicle = async (vid) => {
    if (!window.confirm("Remove this vehicle from your character?")) return;
    try {
      const r = await api.delete(`/characters/${id}/vehicles/${vid}`);
      setC({ ...c, vehicles: r.data.vehicles });
    } catch { toast.error("Failed"); }
  };
  const gearHasEffects = (g) => {
    const e = g?.effects || {};
    return !!(e.heal || e.stress || e.radiation || (Array.isArray(e.stat_boosts) && e.stat_boosts.length) || g.consumable);
  };
  
  return (
    <div className="p-4 sm:p-8 max-w-6xl">
      <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-6">
        <div className="relative">
          <img data-testid="char-token" src={tokenUrl || DEFAULT_TOKEN} alt="" className="w-28 h-28 sm:w-32 sm:h-32 token-img object-cover rounded-full" />
          {isOwner && (
            <>
              <button onClick={() => fileInput.current?.click()} data-testid="upload-token-btn" className="btn-amber mt-2 w-full flex items-center justify-center gap-1 text-xs">
                <Upload size={12}/> Token
              </button>
              <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={uploadToken} />
            </>
          )}
        </div>
        <div className="flex-1 w-full">
          <Field label={`Name ${!canEditName ? "" : ""}`}>
            <input className="input-term heading-mono text-xl sm:text-2xl amber-glow uppercase" data-testid="char-name" value={c.name} onChange={(e) => set("name", e.target.value)} disabled={!canEditName} />
          </Field>
          <div className="mt-3">
            <Field label={`Career ${!canEditCareer ? "" : ""}`}>
              <select className="input-term" data-testid="char-career" value={c.career || ""} onChange={(e) => set("career", e.target.value)} disabled={!canEditCareer}>
                <option value="">— select —</option>
                {CAREERS.map(x => <option key={x} value={x}>{x}</option>)}
              </select>
            </Field>
          </div>
          <div className="mt-4 flex flex-wrap gap-3 mono text-xs items-center">
            <span className="neon-green flex items-center gap-2" data-testid="xp-display">
              XP: {c.experience_points}
              {(isGM || isAdmin) && (
                <span className="flex items-center gap-1 ml-1">
                  <button className="btn-amber text-xs px-1.5 py-0.5" onClick={async()=>{try{const r=await api.post(`/characters/${id}/xp`,{delta:1});setC({...c,experience_points:r.data.experience_points});}catch(e){toast.error(e?.response?.data?.detail||"Failed");}}} data-testid="xp-add"><Plus size={10}/></button>
                  <button className="btn-amber text-xs px-1.5 py-0.5" onClick={async()=>{try{const r=await api.post(`/characters/${id}/xp`,{delta:-1});setC({...c,experience_points:r.data.experience_points});}catch(e){toast.error(e?.response?.data?.detail||"Failed");}}} data-testid="xp-sub"><Minus size={10}/></button>
                </span>
              )}
            </span>
            <span className="amber flex items-center gap-2" data-testid="cash-display">
              Personal: ${c.cash}
              {canEditCash && (
                <span className="flex items-center gap-1">
                  <input className="input-term w-20 py-1 text-xs" type="number" value={cashDelta} onChange={e=>setCashDelta(Number(e.target.value))} data-testid="cash-delta" />
                  <button className="btn-amber text-xs px-2 py-1" onClick={()=>{addCash(Math.abs(cashDelta)); setCashDelta(0);}} data-testid="cash-add-btn"><Plus size={10}/></button>
                  <button className="btn-amber text-xs px-2 py-1" onClick={()=>{addCash(-Math.abs(cashDelta)); setCashDelta(0);}} data-testid="cash-sub-btn"><Minus size={10}/></button>
                </span>
              )}
            </span>
            {campaign && <span className="neon-green">Party: ${campaign.party_cash || 0}</span>}
            <span>Story: {c.story_points}</span>
          </div>
        </div>
        {canEditBasic && <button className="btn-amber flex items-center gap-1" onClick={save} data-testid="char-save-btn"><Save size={14}/> Save</button>}
      </div>

      {/* Inline friend requests for owners */}
      {isOwner && friendsData.requests.length > 0 && (
        <div className="panel p-4 mb-4 border-green" data-testid="inline-friend-requests">
          <div className="label-term neon-green mb-2">// Incoming Friend Requests ({friendsData.requests.length})</div>
          <div className="space-y-1">
            {friendsData.requests.map(r => (
              <div key={r.id} className="flex items-center justify-between mono text-sm">
                <span><span className="amber">{r.name}</span> wants to be friends with <span className="amber">{c.name}</span></span>
                <button className="btn-amber text-xs flex items-center gap-1" onClick={()=>acceptFR(r.id)} data-testid={`inline-accept-${r.id}`}><Check size={12}/> Accept</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-1 mb-4 border-b border-[#333] overflow-x-auto">
        {["stats","inventory","backstory","campaign","friends","gm-notes"].map(t => (
          <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)} className={`px-4 py-2 mono text-xs uppercase tracking-widest whitespace-nowrap ${tab===t?"amber border-b-2 border-[#FFB000]":"text-[#A0A0A0]"}`}>{t.replace("-"," ")}</button>
        ))}
      </div>

      {tab === "stats" && (
        <div className="space-y-6">
          <SpendXPDropdown character={c} setC={setC} />
          {ATTRS.map(a => (
            <div key={a.key} className="panel p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="label-term amber">
                  // {a.name}
                  {buffMap[a.key] ? <span className="buff-badge" data-testid={`buff-badge-${a.key}`}>{buffSign(buffMap[a.key])}</span> : null}
                </div>
                <div className="flex items-center gap-2">
                  <label className="label-term">Attribute</label>
                  <input type="number" data-testid={`attr-${a.key}`} className={`input-term w-20 text-center text-xl ${buffMap[a.key] ? "buff-glow" : ""}`} value={c.attributes?.[a.key] ?? 2} onChange={(e) => setAttr(a.key, e.target.value)} disabled={!canEditStats} />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {a.skills.map(([k, l]) => (
                  <Field key={k} label={l}>
                    <input type="number" data-testid={`skill-${k}`} className="input-term" value={c.skills?.[k] ?? 0} onChange={(e) => setSkill(k, e.target.value)} disabled={!canEditStats} />
                  </Field>
                ))}
              </div>
            </div>
          ))}
          <div className="panel p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label={<span>Health{buffMap.health ? <span className="buff-badge" data-testid="buff-badge-health">{buffSign(buffMap.health)}</span> : null}</span>}><input type="number" className={`input-term ${buffMap.health ? "buff-glow" : ""}`} value={c.health} onChange={e=>set("health",Number(e.target.value))} disabled={!canEditBasic} /></Field>
            <Field label="Max Health"><input type="number" className="input-term" value={c.max_health} onChange={e=>set("max_health",Number(e.target.value))} disabled={!canEditBasic} /></Field>
            <Field label="Stress Level"><input type="number" className="input-term" value={c.stress_level} onChange={e=>set("stress_level",Number(e.target.value))} disabled={!canEditBasic} /></Field>
            <Field label={<span>Resolve{buffMap.resolve ? <span className="buff-badge" data-testid="buff-badge-resolve">{buffSign(buffMap.resolve)}</span> : null}</span>}><input type="number" className={`input-term ${buffMap.resolve ? "buff-glow" : ""}`} value={c.resolve} onChange={e=>set("resolve",Number(e.target.value))} disabled={!canEditBasic} /></Field>
            <Field label="Radiation"><input type="number" className="input-term" value={c.radiation} onChange={e=>set("radiation",Number(e.target.value))} disabled={!canEditBasic} /></Field>
            <Field label="Fatigued"><select className="input-term" value={c.fatigued ? "yes":"no"} onChange={e=>set("fatigued", e.target.value==="yes")} disabled={!canEditBasic}><option value="no">No</option><option value="yes">Yes</option></select></Field>
            <div className="col-span-2"><Field label="Critical Injuries"><input className="input-term" value={c.critical_injuries} onChange={e=>set("critical_injuries",e.target.value)} disabled={!canEditBasic} /></Field></div>
            <Field label="Encumbrance"><input type="number" className="input-term" value={c.encumbrance} onChange={e=>set("encumbrance",Number(e.target.value))} disabled={!canEditBasic} /></Field>
          </div>
        </div>
      )}

      {tab === "inventory" && (
        <div className="space-y-4">
          <div className="panel p-4 mono text-xs text-[#A0A0A0]">
            // Hint: Buy items from the <Link className="amber underline" to="/store">Store</Link> — weapons/armor/gear stats come from the store and are locked to preserve game balance.
          </div>

          <div className="panel p-5">
            <div className="flex justify-between items-center mb-3">
              <div className="label-term amber">// Armor</div>
            </div>
            {(c.armors||[]).length === 0 && <div className="mono text-xs text-[#A0A0A0]">// no armor — visit the Store to purchase.</div>}
            {(c.armors||[]).map((a, i) => (
              <div key={a.id || i} className="mb-3 pb-2 border-b border-[#1a1a1a] last:border-b-0">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
                  <div className="md:col-span-2"><Field label="Name"><input className="input-term" value={a.name} disabled /></Field></div>
                  <Field label="Rating"><input className="input-term" type="number" value={a.level || 0} disabled /></Field>
                  <Field label="Air"><input className="input-term" type="number" value={a.modifier || 0} disabled /></Field>
                  <Field label="Weight"><input className="input-term" type="number" value={a.weight || 0} disabled /></Field>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {a.equipped
                    ? <span className="mono text-[10px] border border-[#4AF626] neon-green px-2 py-0.5">EQUIPPED</span>
                    : (canEditBasic && <button className="btn-amber text-xs" onClick={async ()=>{
                        const next = (c.armors||[]).map((x,j)=>({...x, equipped: j===i}));
                        const eq = next[i];
                        const legacy = {name:eq.name, level:eq.level||0, modifier:eq.modifier||0, encumbrance:eq.encumbrance||0, weight:eq.weight||0};
                        await api.patch(`/characters/${id}`, { armors: next, armor: legacy });
                        const r = await api.get(`/characters/${id}`); setC(r.data);
                      }} data-testid={`equip-armor-${i}`}>Equip</button>)
                  }
                  {canEditBasic && c.campaign_id && <button className="btn-amber text-xs flex items-center gap-1" onClick={()=>stowOnShip("armors", i)} data-testid={`stow-armor-${i}`}><PackagePlus size={12}/> Stow</button>}
                  {canEditBasic && <button className="text-[#FF2A2A] ml-auto" onClick={()=>set("armors", (c.armors||[]).filter((_,j)=>j!==i))} data-testid={`remove-armor-${i}`}><Trash2 size={14}/></button>}
                </div>
              </div>
            ))}
          </div>

          <div className="panel p-5">
            <div className="label-term amber mb-3">// Weapons</div>
            {(c.weapons||[]).length === 0 && <div className="mono text-xs text-[#A0A0A0]">// no weapons — visit the Store to purchase.</div>}
            {(c.weapons||[]).map((w, i) => (
              <div key={i} className="mb-3 pb-2 border-b border-[#1a1a1a] last:border-b-0">
                <div className="grid grid-cols-1 md:grid-cols-7 gap-1 items-end">
                  <div className="md:col-span-2"><Field label="Name"><input className="input-term" value={w.name} disabled /></Field></div>
                  <Field label="Mod"><input className="input-term" type="number" value={w.mod} disabled /></Field>
                  <Field label="Damage"><input className="input-term" type="number" value={w.damage} disabled /></Field>
                  <Field label="Range"><input className="input-term" value={w.range} disabled /></Field>
                  <Field label="Ammo"><input className="input-term" type="number" value={w.ammo} onChange={e=>{const n=[...c.weapons];n[i]={...w,ammo:Number(e.target.value)};set("weapons",n)}} disabled={!canEditBasic} /></Field>
                  <Field label="Weight"><input className="input-term" type="number" value={w.weight} disabled /></Field>
                  {canEditBasic && <button className="text-[#FF2A2A] pb-2" onClick={()=>removeWeapon(i)} data-testid={`remove-weapon-${i}`}><Trash2 size={14}/></button>}
                </div>
                <div className="mt-2 text-sm"><Markdown>{w.description}</Markdown></div>
                {canEditBasic && c.campaign_id && (
                  <div className="mt-2"><button className="btn-amber text-xs flex items-center gap-1" onClick={()=>stowOnShip("weapons", i)} data-testid={`stow-weapon-${i}`}><PackagePlus size={12}/> Stow </button></div>
                )}
              </div>
            ))}
          </div>

          <div className="panel p-5">
            <div className="label-term amber mb-3">// Gear</div>
            {(c.gear||[]).length === 0 && <div className="mono text-xs text-[#A0A0A0]">// no gear — visit the Store to purchase.</div>}
            {(c.gear||[]).map((g, i) => (
              <div key={i} className="mb-3 pb-2 border-b border-[#1a1a1a] last:border-b-0">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
                  <div className="md:col-span-2"><Field label="Name"><input className="input-term" value={g.name} disabled /></Field></div>
                  <Field label="Weight"><input className="input-term" type="number" value={g.weight} disabled /></Field>
                  <Field label="Air/Power"><input className="input-term" type="number" value={g.air_power || 0} onChange={e=>{const n=[...c.gear];n[i]={...g,air_power:Number(e.target.value)};set("gear",n)}} disabled={!canEditBasic} /></Field>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {gearHasEffects(g) && (isOwner || canEditGmFields) && (
                    <button className="btn-amber text-xs" onClick={()=>consumeGear(i)} data-testid={`use-gear-${i}`}>Use</button>
                  )}
                  {(g.effects?.heal) ? <span className="mono text-[10px] border border-[#4AF626] neon-green px-2 py-0.5">+{g.effects.heal} HP</span> : null}
                  {(g.effects?.stress) ? <span className="mono text-[10px] border border-[#4AF626] neon-green px-2 py-0.5">{g.effects.stress>0?"+":""}{g.effects.stress} STRESS</span> : null}
                  {(g.effects?.radiation) ? <span className="mono text-[10px] border border-[#4AF626] neon-green px-2 py-0.5">{g.effects.radiation>0?"+":""}{g.effects.radiation} RAD</span> : null}
                  {(g.effects?.stat_boosts||[]).map((b, bi) => <span key={bi} className="mono text-[10px] border border-[#4AF626] neon-green px-2 py-0.5">{b.label || `${b.stat} ${b.value>=0?"+":""}${b.value}`}</span>)}
                  {g.consumable && <span className="mono text-[10px] border border-[#FF2A2A] text-[#FF2A2A] px-2 py-0.5">CONSUMABLE</span>}
                  {canEditBasic && c.campaign_id && <button className="btn-amber text-xs flex items-center gap-1" onClick={()=>stowOnShip("gear", i)} data-testid={`stow-gear-${i}`}><PackagePlus size={12}/> Stow</button>}
                  {canEditBasic && <button className="text-[#FF2A2A] ml-auto" onClick={()=>removeGear(i)} data-testid={`remove-gear-${i}`}><Trash2 size={14}/></button>}
                </div>
              </div>
            ))}
          </div>

          {(c.temp_buffs||[]).length > 0 && (
            <div className="panel p-5" data-testid="temp-buffs-panel">
              <div className="label-term amber mb-3">// Active Effects</div>
              <div className="flex flex-wrap gap-2">
                {(c.temp_buffs||[]).map(b => (
                  <div key={b.id} className="flex items-center gap-2 border border-[#4AF626] px-2 py-1" data-testid={`buff-${b.id}`}>
                    <span className="mono text-xs neon-green">{b.label || `${b.stat} ${b.value>=0?"+":""}${b.value}`}</span>
                    {b.source && <span className="mono text-[10px] text-[#A0A0A0]">({b.source})</span>}
                    {(isOwner || canEditGmFields) && <button className="text-[#FF2A2A]" onClick={()=>clearBuff(b.id)} data-testid={`clear-buff-${b.id}`}><Trash2 size={12}/></button>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(c.vehicles||[]).length > 0 && (
            <div className="panel p-5" data-testid="vehicles-panel">
              <div className="label-term amber mb-3">// Vehicles</div>
              {(c.vehicles||[]).map((v, vi) => (
                <div key={v.id} className="border border-[#222] p-3 mb-2" data-testid={`vehicle-${v.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="heading-mono amber uppercase">{v.name}</div>
                      <div className="mono text-[10px] text-[#A0A0A0] uppercase tracking-widest">{v.vehicle_type}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {canEditBasic && c.campaign_id && <button className="btn-amber text-xs flex items-center gap-1" onClick={()=>stowOnShip("vehicles", vi)} data-testid={`stow-vehicle-${vi}`}><PackagePlus size={12}/> Stow</button>}
                      {(isOwner || canEditGmFields) && <button className="text-[#FF2A2A]" onClick={()=>removeVehicle(v.id)}><Trash2 size={14}/></button>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2 mono text-[10px]">
                    <span className="border border-[#333] px-2 py-0.5">PAX {v.passengers}</span>
                    <span className="border border-[#333] px-2 py-0.5">MAN {v.maneuverability}</span>
                    <span className="border border-[#333] px-2 py-0.5">SPD {v.speed}</span>
                    <span className="border border-[#333] px-2 py-0.5">HULL {v.hull}</span>
                    <span className="border border-[#333] px-2 py-0.5">ARM {v.armor}</span>
                    {v.weapons && <span className="border border-[#333] px-2 py-0.5">WPN {v.weapons}</span>}
                  </div>
                  {v.description && <div className="mono text-xs text-[#A0A0A0] mt-2">{v.description}</div>}
                </div>
              ))}
            </div>
          )}

          <div className="panel p-5 space-y-3">
            <Field label="Signature Item"><input className="input-term" value={c.signature_item} onChange={e=>set("signature_item",e.target.value)} disabled={!canEditBasic} /></Field>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label-term">Tiny Items</label>
                {canEditBasic && (
                  <button type="button" className="btn-amber text-xs" data-testid="add-tiny-item"
                          onClick={()=>set("tiny_items", [...(Array.isArray(c.tiny_items)?c.tiny_items:[]), { name:"", qty:1 }])}>
                    + Add
                  </button>
                )}
              </div>
              {(!Array.isArray(c.tiny_items) || c.tiny_items.length === 0) && (
                <div className="mono text-xs text-[#A0A0A0]">// no tiny items</div>
              )}
              {Array.isArray(c.tiny_items) && c.tiny_items.map((ti, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end" data-testid={`tiny-item-${i}`}>
                  <div className="col-span-8"><Field label="Name"><input className="input-term" value={ti.name||""} disabled={!canEditBasic} onChange={e=>{const n=[...c.tiny_items];n[i]={...ti,name:e.target.value};set("tiny_items",n)}} /></Field></div>
                  <div className="col-span-3"><Field label="Qty"><input className="input-term" type="number" min={1} value={ti.qty||1} disabled={!canEditBasic} onChange={e=>{const n=[...c.tiny_items];n[i]={...ti,qty:Math.max(1,Number(e.target.value)||1)};set("tiny_items",n)}} /></Field></div>
                  {canEditBasic && <button className="text-[#FF2A2A] pb-2 col-span-1" onClick={()=>set("tiny_items", c.tiny_items.filter((_,x)=>x!==i))} data-testid={`remove-tiny-${i}`}><Trash2 size={14}/></button>}
                </div>
              ))}
            </div>
          </div>

          {/* Inline Shop Purchase */}
          {(isOwner || canEditGmFields) && c.campaign_id && (
            <ShopPurchasePanel character={c} campaign={campaign} canBypass={canEditGmFields} onPurchased={(newPartyCash) => { setCampaign({...campaign, party_cash:newPartyCash}); load(); }} />
          )}
        </div>
      )}

      {tab === "backstory" && (
        <div className="space-y-4">
          <div className="panel p-5">
            <Field label="Talents">
              <textarea className="input-term min-h-[80px]" data-testid="talents-input" value={(c.talents||[]).join("\n")} onChange={e=>set("talents", e.target.value.split("\n"))} disabled={!canEditBasic} placeholder="One talent per line..." />
            </Field>
          </div>
          <div className="panel p-5 space-y-3">
            <Field label="Appearance"><textarea className="input-term min-h-[60px]" value={c.appearance} onChange={e=>set("appearance",e.target.value)} disabled={!canEditBasic} /></Field>
            <Field label="Personal Agenda"><textarea className="input-term min-h-[60px]" value={c.personal_agenda} onChange={e=>set("personal_agenda",e.target.value)} disabled={!canEditBasic} /></Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Buddy">
                <select className="input-term" value={c.buddy || ""} onChange={e=>set("buddy",e.target.value)} disabled={!canEditBasic} data-testid="buddy-select">
                  <option value="">{!c.campaign_id ? "— assign campaign first —" : campaignChars.length === 0 ? "— no other crew yet —" : "— none —"}</option>
                  {campaignChars.map(x => <option key={x.id} value={x.name}>{x.name}</option>)}
                </select>
              </Field>
              <Field label="Rival">
                <select className="input-term" value={c.rival || ""} onChange={e=>set("rival",e.target.value)} disabled={!canEditBasic} data-testid="rival-select">
                  <option value="">{!c.campaign_id ? "— assign campaign first —" : campaignChars.length === 0 ? "— no other crew yet —" : "— none —"}</option>
                  {campaignChars.map(x => <option key={x.id} value={x.name}>{x.name}</option>)}
                </select>
              </Field>
            </div>
          </div>
          <div className="panel p-5">
            <div className="flex items-center justify-between mb-2">
              <label className="label-term amber">// Back Story (Markdown)</label>
              {canEditBasic && (
                <button type="button" className="btn-amber text-xs" onClick={()=>setEditBackstory(!editBackstory)} data-testid="toggle-backstory">
                  {editBackstory ? "Preview" : "Edit"}
                </button>
              )}
            </div>
            {editBackstory && canEditBasic ? (
              <textarea className="input-term min-h-[280px]" value={c.backstory} onChange={e=>set("backstory",e.target.value)} data-testid="backstory-input" placeholder="# Title\n**bold** *italic* - list" />
            ) : (
              <Markdown>{c.backstory}</Markdown>
            )}
          </div>
        </div>
      )}

      {tab === "campaign" && (
        <div className="panel p-5">
          <div className="label-term amber mb-3">// Campaign Assignment</div>
          {campaign ? (
            <div>
              <Link to={`/campaigns/${campaign.id}`} className="heading-mono text-xl amber hover:underline">{campaign.name}</Link>
              <div className="mono text-xs text-[#A0A0A0] mt-1">Game Type: {campaign.game_type}</div>
              <div className="mt-3"><Markdown>{campaign.description}</Markdown></div>
              <div className="mt-3 mono text-xs neon-green">Session: {campaign.session_active ? "ACTIVE" : "IDLE"} · Party Cash: ${campaign.party_cash || 0}</div>
            </div>
          ) : <div className="mono text-[#A0A0A0]">// Not assigned to any campaign.</div>}
        </div>
      )}

      {tab === "friends" && (
        <div className="space-y-4">
          {!isOwner && <div className="panel p-5 mono text-[#A0A0A0]">// Friends list is private to the character's owner.</div>}
          {isOwner && (
            <>
              <div className="panel p-5" data-testid="char-friends-list">
                <div className="label-term amber mb-3">// {c.name}'s Friends ({friendsData.friends.length})</div>
                {friendsData.friends.length === 0 && <div className="mono text-xs text-[#A0A0A0]">// none yet</div>}
                {friendsData.friends.map(f => (
                  <div key={f.id} className="flex items-center justify-between border-b border-[#222] py-2">
                    <Link to="/friends" className="flex items-center gap-2 hover:underline">
                      <img src={f.token_path ? fileUrl(f.token_path) : DEFAULT_TOKEN} alt="" className="w-8 h-8 token-img object-cover rounded-full" />
                      <span className="amber mono">{f.name}</span>
                    </Link>
                  </div>
                ))}
              </div>
              <div className="panel p-5" data-testid="char-requests-list">
                <div className="label-term amber mb-3">// Pending Requests ({friendsData.requests.length})</div>
                {friendsData.requests.length === 0 && <div className="mono text-xs text-[#A0A0A0]">// none</div>}
                {friendsData.requests.map(r => (
                  <div key={r.id} className="flex items-center justify-between border-b border-[#222] py-2">
                    <span className="mono"><span className="amber">{r.name}</span></span>
                    <button className="btn-amber text-xs flex items-center gap-1" onClick={()=>acceptFR(r.id)} data-testid={`char-accept-${r.id}`}><Check size={12}/> Accept</button>
                  </div>
                ))}
              </div>
              <div className="panel p-5 mono text-xs text-[#A0A0A0]">// To send a request, go to <Link to="/friends" className="amber underline">Friends / DM</Link> and pick this character.</div>
            </>
          )}
        </div>
      )}

      {tab === "gm-notes" && (
        <div className="panel p-5">
          <label className="label-term amber mb-2 block">// GM Notes / Agenda</label>
          <textarea className="input-term min-h-[200px]" data-testid="gm-notes-textarea" value={c.gm_notes || ""} onChange={e=>set("gm_notes",e.target.value)} disabled={!canEditGmFields} placeholder={canEditGmFields ? "GM Only" : "Only GM/Admin can edit"} />
        </div>
      )}
    </div>
  );
}

const SKILL_LIST = [
  { key:"close_combat", label:"Close Combat", parent:"strength" },
  { key:"heavy_machinery", label:"Heavy Machinery", parent:"strength" },
  { key:"stamina", label:"Stamina", parent:"strength" },
  { key:"mobility", label:"Mobility", parent:"agility" },
  { key:"ranged_combat", label:"Ranged Combat", parent:"agility" },
  { key:"piloting", label:"Piloting", parent:"agility" },
  { key:"observation", label:"Observation", parent:"wits" },
  { key:"comtech", label:"Comtech", parent:"wits" },
  { key:"survival", label:"Survival", parent:"wits" },
  { key:"manipulation", label:"Manipulation", parent:"empathy" },
  { key:"command", label:"Command", parent:"empathy" },
  { key:"medical_aid", label:"Medical Aid", parent:"empathy" },
];
const ATTR_LIST = [
  { key:"strength", label:"Strength" }, { key:"agility", label:"Agility" },
  { key:"wits", label:"Wits" }, { key:"empathy", label:"Empathy" },
];

function SpendXPDropdown({ character, setC }) {
  const [type, setType] = useState("attribute");
  const [key, setKey] = useState("strength");
  const xp = character.experience_points || 0;
  const skills = character.skills || {};
  const cost = type === "attribute"
    ? 5
    : ((Number(skills[key] || 0) + 1) * 5);
  const canAfford = xp >= cost;
  const spend = async () => {
    try {
      const r = await api.post(`/characters/${character.id}/spend-xp`, { target_type: type, key, levels: 1 });
      toast.success(`+1 ${key.replace("_"," ")} (−${r.data.cost} XP)`);
      if (type === "attribute") {
        setC({ ...character, experience_points: r.data.experience_points, attributes: { ...character.attributes, [key]: r.data.value } });
      } else {
        setC({ ...character, experience_points: r.data.experience_points, skills: { ...character.skills, [key]: r.data.value } });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="panel p-4 flex flex-col sm:flex-row sm:items-end gap-3" data-testid="spend-xp-panel">
      <div className="flex-1">
        <div className="label-term amber mb-1">// Spend XP</div>
        <div className="mono text-xs text-[#A0A0A0]">Attributes cost 5 XP. Skills cost (current level + 1) × 5 XP.</div>
      </div>
      <div>
        <label className="label-term mb-1 block">Type</label>
        <select className="input-term" value={type} onChange={e=>{ setType(e.target.value); setKey(e.target.value==="attribute"?"strength":"close_combat"); }} data-testid="spend-xp-type">
          <option value="attribute">Attribute</option>
          <option value="skill">Skill</option>
        </select>
      </div>
      <div>
        <label className="label-term mb-1 block">{type === "attribute" ? "Attribute" : "Skill"}</label>
        <select className="input-term" value={key} onChange={e=>setKey(e.target.value)} data-testid="spend-xp-key">
          {(type === "attribute" ? ATTR_LIST : SKILL_LIST).map(o => (
            <option key={o.key} value={o.key}>{o.label}{type === "skill" ? ` (current ${skills[o.key] || 0})` : ""}</option>
          ))}
        </select>
      </div>
      <div className="mono text-xs">
        <div className="text-[#A0A0A0]">Cost</div>
        <div className={canAfford ? "neon-green" : "text-[#FF2A2A]"}>{cost} XP</div>
      </div>
      <div className="mono text-xs">
        <div className="text-[#A0A0A0]">Available</div>
        <div className="neon-green">{xp} XP</div>
      </div>
      <button className="btn-amber text-xs" disabled={!canAfford} onClick={spend} data-testid="spend-xp-submit">+1 Level</button>
    </div>
  );
}

